/**
 * bigwaToast — Wrapper sobre react-hot-toast com semântica Bigwa
 *
 * Padroniza: estilo, duração, fila (max 3), deduplicação por id,
 * e diferenciação visual via borda esquerda colorida.
 */
import toast from 'react-hot-toast';
import React from 'react';
import { BigwaToast } from '../components/BigwaToast';
import type { ButtonIntent } from '../components/BigwaToast';
import { useNotificationPanelStore } from '../stores/notificationPanelStore';
import type { NotificationStatus, NotificationAction } from '../stores/notificationPanelStore';

// ---------------------------------------------------------------------------
// Duration tiers
// ---------------------------------------------------------------------------
export const DURATION = {
  fleeting: 1500,   // "Copiado!", feedback trivial
  standard: 3000,   // Sucesso de save, create, update
  important: 5000,  // Erros, warnings, undo actions
  critical: 8000,   // Quota cheia, prompts multi-ação
  persistent: 0,    // Sessão expirada (infinito — requer ação)
} as const;

export type DurationTier = keyof typeof DURATION;

// ---------------------------------------------------------------------------
// Variant → border-left color mapping
// ---------------------------------------------------------------------------
const VARIANT_BORDER: Record<string, string> = {
  success: '#477B72',
  error: '#D4918A',
  warning: '#F2BC41',
};

// ---------------------------------------------------------------------------
// Inline SVG icons (16px, stroke-only, quiet luxury)
// ---------------------------------------------------------------------------
// Cores mais claras para contraste no fundo #323232 (ratio ≥ 3.5:1)
const ICONS = {
  success: React.createElement('svg', {
    width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    style: { flexShrink: 0 },
  },
    React.createElement('circle', { cx: 8, cy: 8, r: 7, stroke: '#5FA898', strokeWidth: 2 }),
    React.createElement('path', { d: 'M5 8.5L7 10.5L11 5.5', stroke: '#5FA898', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }),
  ),
  error: React.createElement('svg', {
    width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    style: { flexShrink: 0 },
  },
    React.createElement('circle', { cx: 8, cy: 8, r: 7, stroke: '#E5ADA7', strokeWidth: 2 }),
    React.createElement('path', { d: 'M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5', stroke: '#E5ADA7', strokeWidth: 2, strokeLinecap: 'round' }),
  ),
  warning: React.createElement('svg', {
    width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    style: { flexShrink: 0 },
  },
    React.createElement('path', { d: 'M8 1.5L14.5 13.5H1.5L8 1.5Z', stroke: '#F5CB66', strokeWidth: 2, strokeLinejoin: 'round' }),
    React.createElement('path', { d: 'M8 6.5V9.5', stroke: '#F5CB66', strokeWidth: 2, strokeLinecap: 'round' }),
    React.createElement('circle', { cx: 8, cy: 11.5, r: 0.75, fill: '#F5CB66' }),
  ),
};

// ---------------------------------------------------------------------------
// Queue management (max 3 visible toasts)
// ---------------------------------------------------------------------------
const MAX_VISIBLE = 3;

/** IDs of toasts that have action buttons and should not be auto-dismissed by the queue */
const actionToastIds = new Set<string>();

function enforceQueue(): void {
  // react-hot-toast exposes its internal store via toast module
  // We access active toasts through the toast API
  const allToasts = (toast as unknown as { toasts?: Array<{ id: string; visible: boolean }> }).toasts;
  if (!allToasts) return;

  const visible = allToasts.filter(t => t.visible);
  if (visible.length < MAX_VISIBLE) return;

  // Find the oldest non-action toast to dismiss
  for (const t of visible) {
    if (!actionToastIds.has(t.id)) {
      toast.dismiss(t.id);
      return;
    }
  }
}

// ---------------------------------------------------------------------------
// Style builder
// ---------------------------------------------------------------------------
function variantStyle(variant?: string): React.CSSProperties {
  const borderColor = variant ? VARIANT_BORDER[variant] : undefined;
  return borderColor
    ? { borderLeft: `3px solid ${borderColor}`, paddingLeft: '14px' }
    : {};
}

// ---------------------------------------------------------------------------
// Options normalization
// ---------------------------------------------------------------------------
interface BigwaToastOptions {
  id?: string;
  duration?: DurationTier | number;
  icon?: React.ReactElement | string | null;
}

function resolveDuration(d?: DurationTier | number): number {
  if (d === undefined) return DURATION.standard;
  if (typeof d === 'number') return d;
  return DURATION[d];
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function success(message: string, opts?: BigwaToastOptions): string {
  enforceQueue();
  return toast.success(message, {
    id: opts?.id,
    duration: resolveDuration(opts?.duration ?? 'standard'),
    icon: opts?.icon ?? ICONS.success,
    style: variantStyle('success'),
  });
}

function error(message: string, opts?: BigwaToastOptions): string {
  enforceQueue();
  return toast.error(message, {
    id: opts?.id,
    duration: resolveDuration(opts?.duration ?? 'important'),
    icon: opts?.icon ?? ICONS.error,
    style: variantStyle('error'),
  });
}

function warning(message: string, opts?: BigwaToastOptions): string {
  enforceQueue();
  return toast(message, {
    id: opts?.id,
    duration: resolveDuration(opts?.duration ?? 'important'),
    icon: opts?.icon ?? ICONS.warning,
    style: variantStyle('warning'),
  });
}

function info(message: string, opts?: BigwaToastOptions): string {
  enforceQueue();
  return toast(message, {
    id: opts?.id,
    duration: resolveDuration(opts?.duration ?? 'standard'),
  });
}

function loading(message: string, opts?: BigwaToastOptions): string {
  enforceQueue();
  return toast.loading(message, {
    id: opts?.id,
    duration: opts?.duration ? resolveDuration(opts.duration) : undefined,
  });
}

// ---------------------------------------------------------------------------
// Action toast (with buttons)
// ---------------------------------------------------------------------------
interface ActionButton {
  label: string;
  onClick: () => void;
  intent?: ButtonIntent;
}

interface ActionOptions {
  message: string;
  description?: string;
  primaryAction?: ActionButton;
  secondaryAction?: ActionButton;
  variant?: 'default' | 'success' | 'error' | 'warning';
  id?: string;
  duration?: DurationTier | number;
}

function action(opts: ActionOptions): string {
  enforceQueue();
  const dur = resolveDuration(opts.duration ?? 'important');
  const toastId = toast(
    (t) => React.createElement(BigwaToast, {
      t,
      message: opts.message,
      description: opts.description,
      primaryAction: opts.primaryAction,
      secondaryAction: opts.secondaryAction,
      variant: opts.variant,
    }),
    {
      id: opts.id,
      duration: dur,
      style: {
        padding: 0,
        background: 'transparent',
        boxShadow: 'none',
        maxWidth: '400px',
      },
    },
  );
  actionToastIds.add(toastId);
  // Clean up tracking when toast naturally expires
  if (dur > 0) {
    setTimeout(() => actionToastIds.delete(toastId), dur + 500);
  }
  return toastId;
}

// ---------------------------------------------------------------------------
// Utility pass-throughs
// ---------------------------------------------------------------------------
function dismiss(id?: string): void {
  if (id) {
    actionToastIds.delete(id);
    toast.dismiss(id);
  } else {
    toast.dismiss();
  }
}

function dismissAll(): void {
  actionToastIds.clear();
  toast.dismiss();
}

// ---------------------------------------------------------------------------
// Notification Panel (accumulated notifications — Paperpile pattern)
// ---------------------------------------------------------------------------
function notify(message: string, opts?: {
  id?: string;
  status?: NotificationStatus;
  action?: NotificationAction;
  group?: string;
}): string {
  const store = useNotificationPanelStore.getState();
  const id = opts?.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  store.addItem({
    id,
    message,
    status: opts?.status || 'success',
    action: opts?.action,
    group: opts?.group,
  });
  return id;
}

function notifyUpdate(id: string, updates: {
  message?: string;
  status?: NotificationStatus;
  action?: NotificationAction | undefined;
}): void {
  useNotificationPanelStore.getState().updateItem(id, updates);
}

function notifyBatch(title: string, items: Array<{
  id?: string;
  message: string;
  status?: NotificationStatus;
  action?: NotificationAction;
}>): void {
  const store = useNotificationPanelStore.getState();
  store.setHeaderTitle(title);
  for (const item of items) {
    store.addItem({
      id: item.id || `notif-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      message: item.message,
      status: item.status || 'success',
      action: item.action,
    });
  }
}

// ---------------------------------------------------------------------------
// Panel error shorthand (Fase 2d — operation errors go to panel, not toast)
// ---------------------------------------------------------------------------
function notifyError(message: string, opts?: {
  id?: string;
  action?: NotificationAction;
  group?: string;
}): string {
  return notify(message, { ...opts, status: 'error' });
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------
export const bigwaToast = {
  success,
  error,
  warning,
  info,
  loading,
  action,
  dismiss,
  dismissAll,
  notify,
  notifyUpdate,
  notifyBatch,
  notifyError,
};
